/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { ChangeEvent, FocusEvent, useCallback, useMemo } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FormControl, IconButton, InputLabel, OutlinedInput } from '@mui/material';

import { InputTypes } from '../../../../../../constants/inputTypes';
import { useCommonFormWidgetStyles, useTextFormWidgetStyles } from '../../../../../../styles/customFormWidgets';
import { useFormStyles } from '../../../../../../styles/form';
import { isNumeric } from '../../../../../../utils/isNumeric';
import { CustomWidgetProps } from '../../ParametersTab.types';
import { INPUT_NUMBER_TYPE_ALTERNATIVES } from './CustomTextWidget.consts';

const isSafari =
  /constructor/i.test(window.HTMLElement as any) ||
  ((p: any) => p && p.toString() === '[object SafariRemoteNotification]')(
    !(window as any)['safari'] ||
      (typeof (window as any).safari !== 'undefined' && (window as any).safari.pushNotification),
  );

type CustomTextWidgetProps = CustomWidgetProps & { rawErrors: string[] };

// eslint-disable-next-line
export const CustomTextWidget = (props: CustomTextWidgetProps) => {
  const {
    id,
    required,
    readonly,
    disabled,
    label,
    value,
    onChange,
    onBlur,
    onFocus,
    autofocus,
    options,
    schema,
    rawErrors,
    formContext,
  } = props;

  const currentValue = value;

  const name = useMemo(() => label.split('[')[0], [label]);

  const formClasses = useFormStyles();
  const commonWidgetClasses = useCommonFormWidgetStyles();
  const textWidgetClasses = useTextFormWidgetStyles();

  const schemaType = useMemo(() => {
    const currentSchemaType = schema.type as string;
    if (INPUT_NUMBER_TYPE_ALTERNATIVES.indexOf(currentSchemaType) !== -1) {
      return InputTypes.Number;
    } else if (Object.values(InputTypes).indexOf(currentSchemaType) !== -1) {
      return currentSchemaType;
    }
    return InputTypes.Text;
  }, [schema.type]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (formContext?.handleMenuOpen) {
      formContext.handleMenuOpen({ event, name, value: value || null });
    }
  };

  const handleOnChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => onChange(value === '' ? options.emptyValue : value),
    [onChange, options.emptyValue],
  );

  const handleOnChangeSafari = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (value === '') {
        onChange(options.emptyValue);
        return;
      }

      if (schemaType === InputTypes.Number) {
        if (schemaType === InputTypes.Number) {
          const preparedValue = `${value}`.replace(',', '.');
          onChange(isNumeric(preparedValue) ? preparedValue : currentValue);
        } else {
          onChange(value);
        }
      }
    },
    [currentValue, onChange, options.emptyValue, schemaType],
  );

  const handleBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => onBlur(id, value),
    [id, onBlur],
  );

  const handleFocus = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => onFocus(id, value),
    [id, onFocus],
  );

  return (
    <FormControl
      className={cx(formClasses.formControl, textWidgetClasses.fieldControl)}
      error={!!rawErrors && !!rawErrors.length}
      variant="outlined"
    >
      <InputLabel htmlFor={label}>{label}</InputLabel>
      <OutlinedInput
        id={id}
        name={label}
        label={label}
        value={value ?? ''}
        onChange={isSafari ? handleOnChangeSafari : handleOnChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled || readonly}
        type={schemaType === InputTypes.Number && isSafari ? InputTypes.Text : schemaType}
        autoFocus={autofocus}
        required={required}
        className={textWidgetClasses.field}
        endAdornment={
          <IconButton
            size="small"
            className={cx(commonWidgetClasses.widgetMenuButton, textWidgetClasses.widgetMenuButton)}
            aria-controls="menu"
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreHorizIcon />
          </IconButton>
        }
      />
    </FormControl>
  );
};
